import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { Badge, TypographySubtle } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolRadioGroupField } from '#src/components/validated-tool-form/shared/ValidatedToolRadioGroupField.tsx'
import { ValidatedToolResponseCard } from '#src/components/validated-tool-form/shared/ValidatedToolResponseCard.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface FoodSecurityFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const yesNoUnknownEnum = z.enum(['yes', 'no', 'unknown']).nullable()
const oftenSometimesNeverEnum = z
  .enum(['often', 'sometimes', 'never', 'unknown'])
  .nullable()

const schema = z.object({
  HH3: oftenSometimesNeverEnum,
  HH4: oftenSometimesNeverEnum,
  AD1: yesNoUnknownEnum,
  AD1a: z.enum(['threeOrMore', 'zeroToTwo']).nullable(),
  AD2: yesNoUnknownEnum,
  AD3: yesNoUnknownEnum,
  summary: z.string().nullable(),
})

const validatedTool = new ValidatedTool(
  questionnaires.foodSecurityQuestionnaire
)

export function FoodSecurityForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: FoodSecurityFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  const showAD1a = form.watch('AD1') === 'yes'

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      form={form}
      subtitle={validatedTool.getItemText('instructions')}
      response={response}
      {...delegated}
    >
      {!!response && (
        <ValidatedToolResponseCard response={response}>
          <Score
            form={form}
            validatedTool={validatedTool}
            response={response}
          />
          <FormField
            control={form.control}
            name="summary"
            render={({ field: { value, ...rest } }) =>
              !value ? null : (
                <RichTextEditor
                  defaultValue={value}
                  variant="unstyled"
                  placeholder="Add summary…"
                  contentClassName="max-w-(--container-prose)"
                  {...rest}
                  disabled={false}
                  editable={false}
                />
              )
            }
          />
        </ValidatedToolResponseCard>
      )}
      <ValidatedToolSection title="HH3. The food that (I/we) bought just didn't last, and (I/we) didn't have money to get more.">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="HH3"
          suggestion={fieldSuggestions?.HH3}
          options={[
            {
              value: 'often',
              label: 'Often true (1)',
            },
            {
              value: 'sometimes',
              label: 'Sometimes true (1)',
            },
            {
              value: 'never',
              label: 'Never true (0)',
            },
            {
              value: 'unknown',
              label: 'DK or Refused',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="HH4. (I/we) couldn't afford to eat balanced meals.">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="HH4"
          suggestion={fieldSuggestions?.HH4}
          options={[
            {
              value: 'often',
              label: 'Often true (1)',
            },
            {
              value: 'sometimes',
              label: 'Sometimes true (1)',
            },
            {
              value: 'never',
              label: 'Never true (0)',
            },
            {
              value: 'unknown',
              label: 'DK or Refused',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="AD1. In the last 30 days, did (you/you or other adults in your household) ever cut the size of your meals or skip meals because there wasn't enough money for food?">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="AD1"
          suggestion={fieldSuggestions?.AD1}
          options={[
            {
              value: 'yes',
              label: 'Yes (1)',
            },
            {
              value: 'no',
              label: 'No (0)',
            },
            {
              value: 'unknown',
              label: 'DK or Refused',
            },
          ]}
        />
      </ValidatedToolSection>
      {showAD1a && (
        <ValidatedToolSection title="AD1a. [IF YES TO AD1] How many days did this happen?">
          <ValidatedToolRadioGroupField
            control={form.control}
            name="AD1a"
            suggestion={fieldSuggestions?.AD1a}
            options={[
              {
                value: 'threeOrMore',
                label: '3 or more days (1)',
              },
              {
                value: 'zeroToTwo',
                label: '0-2 days (0)',
              },
            ]}
          />
        </ValidatedToolSection>
      )}
      <ValidatedToolSection title="AD2. In the last 30 days, did you ever eat less than you felt you should because there wasn't enough money for food?">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="AD2"
          suggestion={fieldSuggestions?.AD2}
          options={[
            {
              value: 'yes',
              label: 'Yes (1)',
            },
            {
              value: 'no',
              label: 'No (0)',
            },
            {
              value: 'unknown',
              label: 'DK or Refused',
            },
          ]}
        />
      </ValidatedToolSection>
      <ValidatedToolSection title="AD3. In the last 30 days, were you ever hungry but didn't eat because there wasn't enough money for food?">
        <ValidatedToolRadioGroupField
          control={form.control}
          name="AD3"
          suggestion={fieldSuggestions?.AD3}
          options={[
            {
              value: 'yes',
              label: 'Yes (1)',
            },
            {
              value: 'no',
              label: 'No (0)',
            },
            {
              value: 'unknown',
              label: 'DK or Refused',
            },
          ]}
        />
      </ValidatedToolSection>
      <Score form={form} validatedTool={validatedTool} response={response} />
      <ValidatedToolSection title="Summary">
        <FormField
          control={form.control}
          name="summary"
          render={({ field: { value, ...rest } }) => (
            <RichTextEditor
              defaultValue={value ?? ''}
              placeholder="Add summary…"
              contentClassName="max-w-(--container-prose)"
              {...rest}
            />
          )}
        />
      </ValidatedToolSection>
      {!response && (
        <>
          <div>
            <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
          </div>
          <TypographySubtle className="text-foreground-secondary">
            {validatedTool.getItemText('concluding-instructions')}
          </TypographySubtle>
        </>
      )}
    </ValidatedToolInnerForm>
  )
}

const SECURITY_COLORS = {
  'High Food Security': 'green',
  'Marginal Food Security': 'yellow',
  'Low Food Security': 'orange',
  'Very Low Food Security': 'red',
} as const satisfies Record<questionnaires.FoodSecurityCategory, string>

const HDM_COLORS = {
  'Not Eligible': 'green',
  'Eligible to Continue; Not to Start': 'yellow',
  Eligible: 'red',
} as const satisfies Record<questionnaires.HomeDeliveredMealEligibility, string>

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  const score = result.score
  const securityLabel = questionnaires.foodSecurityCategory(score)
  const securityColor = securityLabel
    ? SECURITY_COLORS[securityLabel]
    : ('gray' as const)
  const hdmLabel = questionnaires.homeDeliveredMealEligibility(score)
  const hdmColor = hdmLabel ? HDM_COLORS[hdmLabel] : ('gray' as const)

  return (
    <div className="flex flex-col gap-2">
      <h2>
        Score: <b>{score}</b>
      </h2>
      <div className="flex items-center gap-2">
        Food Security Level:
        <Badge color={securityColor} className="mt-px">
          {securityLabel ?? 'Not Scored'}
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        Home-Delivered Meal Eligibility:
        <Badge color={hdmColor} className="mt-px">
          {hdmLabel ?? 'Not Determined'}
        </Badge>
      </div>
    </div>
  )
}
